import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";
import { Section } from "@/components/Section";
import {
  Inbox,
  Calendar,
  CheckCircle2,
  XCircle,
  Hourglass,
  Building2,
  Mail,
  ArrowUpRight,
  LogOut,
  ShieldCheck,
  ClipboardList,
  Users2,
  Calculator,
  RefreshCcw,
  Copy,
  ListTodo,
  Circle,
  KeyRound,
  AlertTriangle,
  Boxes,
  UserPlus,
  ExternalLink,
  X,
} from "lucide-react";

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [demos, setDemos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [accessReqs, setAccessReqs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [actor, setActor] = useState(
    (typeof window !== "undefined" && window.localStorage?.getItem("nodera_admin_actor")) || user?.email || "admin"
  );
  const [reasonById, setReasonById] = useState({});
  const [credentialPopup, setCredentialPopup] = useState(null);
  const [todos, setTodos] = useState([]);
  const [emailHealth, setEmailHealth] = useState(null);
  const [seedingPortal, setSeedingPortal] = useState(false);
  const [portalStatus, setPortalStatus] = useState(null); // {ok, clinic:{present}, vendor:{present, directory_record_linked}}
  const [systemHealth, setSystemHealth] = useState(null);
  const [vendorDirectory, setVendorDirectory] = useState([]);
  const [recruitBucket, setRecruitBucket] = useState(null); // null | 'sleep' | ...
  const [notifierHealth, setNotifierHealth] = useState(null);

  // Memoise the open-todos slice — referenced 4× per render in the
  // pending-tasks card (list, empty state, overflow note ×2).
  const openTodos = useMemo(() => todos.filter((t) => !t.done), [todos]);

  const loadPortalStatus = async () => {
    try {
      const r = await api.get("/auth/admin/portal-logins-status");
      setPortalStatus(r.data);
    } catch {
      setPortalStatus({ ok: false, _error: true });
    }
  };

  const loadSystemHealth = async () => {
    try {
      const r = await api.get("/admin/system-health");
      setSystemHealth(r.data);
    } catch {
      setSystemHealth({ _error: true });
    }
  };

  const seedPortalLogins = async () => {
    if (
      !window.confirm(
        "This will (re)create the shared clinic + vendor portal logins:\n\n" +
          "  · admin+clinic@noderahealth.com  / MyNHLogin123!!\n" +
          "  · admin+vendor@noderahealth.com  / MyNHLogin123!!\n\n" +
          "Safe to run on any environment. Existing accounts will have their password reset.\n\n" +
          "Continue?",
      )
    ) {
      return;
    }
    setSeedingPortal(true);
    try {
      const r = await api.post("/auth/admin/seed-portal-logins");
      const d = r.data || {};
      toast.success(
        `Portal logins ready · clinic ${d.clinic?.action} · vendor ${d.vendor?.action}. Password: MyNHLogin123!!`,
        { duration: 8000 },
      );
      loadPortalStatus();
      loadSystemHealth();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSeedingPortal(false);
    }
  };

  const loadEmailHealth = async () => {
    try {
      const [s, q] = await Promise.all([
        api.get("/admin/email/resend-config"),
        api.get("/admin/email/queue"),
      ]);
      setEmailHealth({ status: s.data, queue: q.data });
    } catch {
      // silent
    }
  };
  useEffect(() => {
    loadEmailHealth();
  }, []);

  const loadTodos = async () => {
    try {
      const r = await api.get("/admin-todos");
      setTodos(r.data || []);
    } catch {
      // silent — todos endpoint may be unreachable mid-session
    }
  };
  useEffect(() => {
    loadTodos();
  }, []);

  useEffect(() => {
    loadPortalStatus();
  }, []);

  useEffect(() => {
    loadSystemHealth();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [d, v, a, vd, nh] = await Promise.all([
        api.get("/demo"),
        api.get("/admin/vendor-applications"),
        api.get("/clinic-access-requests"),
        api.get("/vendors").catch(() => ({ data: [] })),
        api.get("/admin/notifier-health").catch(() => ({ data: null })),
      ]);
      setDemos(d.data || []);
      setVendors(v.data || []);
      setAccessReqs(a.data || []);
      setVendorDirectory(vd.data || []);
      setNotifierHealth(nh.data || null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (actor) localStorage.setItem("nodera_admin_actor", actor);
  }, [actor]);

  const approve = async (req) => {
    try {
      const r = await api.post(`/clinic-access-request/${req.id}/approve`, {});
      toast.success(`Approved ${req.work_email}.`);
      setCredentialPopup({
        email: r.data.email,
        temp_password: r.data.temp_password,
      });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const reject = async (req) => {
    try {
      await api.post(`/clinic-access-request/${req.id}/reject`, {
        reason: reasonById[req.id] || null,
      });
      toast.success(`Rejected ${req.work_email}.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const counts = {
    pendingAccess: accessReqs.filter((r) => r.status === "pending").length,
    pendingVendors: vendors.filter((v) => v.status === "submitted" || v.status === "needs_clarification").length,
    demos: demos.length,
    demosWithRoi: demos.filter((d) => d.roi_model).length,
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied.");
    } catch {
      toast.error("Copy failed — select manually.");
    }
  };

  return (
    <div data-testid="admin-dashboard-page">
      <Section className="pt-28 pb-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600 mb-3">Admin · Internal</div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900 leading-[1.05]">
              Command Center
            </h1>
            <p className="mt-2 text-slate-600 max-w-xl">
              Prioritized view for <span className="font-semibold text-slate-900">{user?.email}</span>. Action items first, in-flight work second, reference last.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              value={actor}
              onChange={(e) => setActor(e.target.value)}
              placeholder="Actor"
              data-testid="admin-dashboard-actor-input"
              className="w-40 bg-white border border-slate-200 rounded-full px-3.5 py-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            />
            <button
              type="button"
              onClick={seedPortalLogins}
              disabled={seedingPortal}
              data-testid="admin-dashboard-seed-portal-logins-btn"
              title="Create/refresh the shared admin+clinic and admin+vendor portal logins (idempotent, safe to re-run on any env)"
              className="inline-flex items-center gap-1.5 bg-white border border-blue-200 text-blue-700 rounded-full px-4 py-2 text-sm font-medium hover:bg-blue-50 disabled:opacity-50"
            >
              <KeyRound className="w-3.5 h-3.5" />{" "}
              {seedingPortal ? "Seeding…" : "Seed portal logins"}
            </button>
            <button
              type="button"
              onClick={load}
              data-testid="admin-dashboard-refresh-btn"
              className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2 text-sm font-medium hover:bg-slate-50"
            >
              <RefreshCcw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button
              type="button"
              onClick={() => {
                logout();
                navigate("/admin", { replace: true });
              }}
              data-testid="admin-dashboard-logout-btn"
              className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-slate-800"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>

        {/* ── System integration health strip ── */}
        <SystemHealthStrip
          health={systemHealth}
          portalStatus={portalStatus}
          onRefresh={() => {
            loadSystemHealth();
            loadPortalStatus();
          }}
        />


        {/* ── 1. NOW — action required ── */}
        <PriorityBand
          tone="urgent"
          eyebrow="Now"
          subtitle="Action required"
          testId="admin-band-now"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Stat icon={Inbox} label="Pending access requests" value={counts.pendingAccess} testId="admin-stat-pending-access" tone="urgent" />
            <Stat icon={ShieldCheck} label="Pending vendor apps" value={counts.pendingVendors} testId="admin-stat-pending-vendors" tone="urgent" />
            {emailHealth && !emailHealth.status?.configured && (
              <Stat
                icon={Inbox}
                label="Pending email deliveries"
                value={
                  (emailHealth.queue?.auth_outbox?.pending || 0) +
                  (emailHealth.queue?.vendor_pipeline_outbox?.pending || 0) +
                  (emailHealth.queue?.quick_actions?.pending || 0)
                }
                testId="admin-stat-pending-emails"
                tone="urgent"
              />
            )}
          </div>
        </PriorityBand>

        {/* ── 2. TODAY — in-flight ── */}
        <PriorityBand
          tone="active"
          eyebrow="Today"
          subtitle="In-flight"
          testId="admin-band-today"
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat icon={Calendar} label="Demo requests" value={counts.demos} testId="admin-stat-demos" />
            <Stat icon={Calculator} label="Demos with ROI model" value={counts.demosWithRoi} testId="admin-stat-demos-roi" />
            <Link
              to="/admin/demos"
              data-testid="admin-link-demos"
              className="rounded-2xl border border-slate-200 bg-white p-4 hover:bg-slate-50 transition-colors flex items-center gap-3"
            >
              <span className="w-9 h-9 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center">
                <ClipboardList className="w-4 h-4 text-slate-700" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400">Open</div>
                <div className="text-sm font-bold text-slate-900 truncate">Demo requests</div>
              </div>
              <ArrowUpRight className="w-3.5 h-3.5 text-slate-400" />
            </Link>
            <Link
              to="/admin/vendors"
              data-testid="admin-link-vendors"
              className="rounded-2xl border border-slate-200 bg-white p-4 hover:bg-slate-50 transition-colors flex items-center gap-3"
            >
              <span className="w-9 h-9 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center">
                <Users2 className="w-4 h-4 text-slate-700" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400">Open</div>
                <div className="text-sm font-bold text-slate-900 truncate">Vendor pipeline</div>
              </div>
              <ArrowUpRight className="w-3.5 h-3.5 text-slate-400" />
            </Link>
          </div>
        </PriorityBand>

        {/* ── 3. REFERENCE — longer-horizon, status, history ── */}
        <PriorityBand
          tone="reference"
          eyebrow="Reference"
          subtitle="System status & backlog"
          testId="admin-band-reference"
        >
          <NotifierHealthPanel data={notifierHealth} />
          <VendorCoverageWidget
            vendors={vendorDirectory}
            applications={vendors}
            onRecruit={setRecruitBucket}
          />
          {recruitBucket && (
            <RecruitDrawer
              bucket={recruitBucket}
              applications={vendors}
              onClose={() => setRecruitBucket(null)}
            />
          )}

          {emailHealth && (
            <EmailHealthPanel
              data={emailHealth}
              onDrain={async () => {
                try {
                  const r = await api.post("/admin/email/drain-now", {});
                  if (r.data?.skipped_reason) toast.message(r.data.skipped_reason);
                  else toast.success(`Drained: ${r.data.sent_total || 0} email(s) sent.`);
                  loadEmailHealth();
                } catch {
                  toast.error("Drain failed.");
                }
              }}
              onRefresh={loadEmailHealth}
            />
          )}

          <div className="mt-6" data-testid="admin-todo-section">
            <div className="flex items-end justify-between mb-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400 inline-flex items-center gap-1.5">
                  <ListTodo className="w-3.5 h-3.5" /> Admin To Do
                </div>
                <h2 className="font-display text-xl font-bold text-slate-900 mt-1">Pending operational tasks</h2>
              </div>
              <Link
                to="/admin/todos"
                data-testid="admin-todo-open-link"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-900 bg-white border border-slate-200 rounded-full px-3.5 py-1.5 hover:bg-slate-50"
              >
                Open To Do List <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
            <ul className="space-y-2" data-testid="admin-todo-list">
              {openTodos
                .slice(0, 4)
                .map((t) => (
                  <li
                    key={t.id}
                    data-testid={`admin-todo-${t.id}`}
                    className="rounded-2xl border bg-white p-4 flex items-start gap-3 border-slate-200"
                  >
                    <Circle className="mt-0.5 w-5 h-5 text-slate-300 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-semibold text-slate-900">{t.title}</div>
                      {t.detail && (
                        <p className="mt-1 text-sm text-slate-600 leading-relaxed line-clamp-2">{t.detail}</p>
                      )}
                    </div>
                  </li>
                ))}
              {openTodos.length === 0 && (
                <li className="text-sm text-slate-500 rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center">
                  No pending tasks. 🎉
                </li>
              )}
            </ul>
            {openTodos.length > 4 && (
              <p className="mt-2 text-xs text-slate-500">
                Showing 4 of {openTodos.length} open. View full list →
              </p>
            )}
          </div>
        </PriorityBand>
      </Section>

      {/* Clinic access requests — central queue */}
      <Section className="pt-0">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400">Vetting queue</div>
            <h2 className="font-display text-2xl font-bold text-slate-900">Clinic access requests</h2>
          </div>
          <span className="text-xs text-slate-500">{accessReqs.length} total</span>
        </div>

        {loading && <div className="text-sm text-slate-500">Loading…</div>}

        {!loading && accessReqs.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
            No clinic access requests yet.
          </div>
        )}

        <div className="space-y-3">
          {accessReqs.map((r) => (
            <div
              key={r.id}
              data-testid={`admin-access-row-${r.id}`}
              className="rounded-2xl border border-slate-200 bg-white p-5"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <span className="w-10 h-10 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                    <Building2 className="w-4 h-4 text-slate-700" />
                  </span>
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-slate-900 truncate">{r.clinic_name}</div>
                    <div className="text-xs text-slate-500 truncate flex items-center gap-1.5 mt-0.5">
                      <Mail className="w-3 h-3" /> {r.work_email} · {r.contact_name}
                    </div>
                    <div className="mt-1 text-[10.5px] uppercase tracking-[0.16em] font-bold text-slate-500">
                      {r.clinic_type}
                    </div>
                    {r.why && <p className="mt-2 text-sm text-slate-700 leading-relaxed">{r.why}</p>}
                  </div>
                </div>
                <div className="text-xs text-slate-500 whitespace-nowrap shrink-0">{fmt(r.created_at)}</div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <StatusBadge status={r.status} />
                {r.status === "pending" ? (
                  <>
                    <button
                      type="button"
                      onClick={() => approve(r)}
                      data-testid={`admin-access-approve-${r.id}`}
                      className="inline-flex items-center gap-1.5 bg-emerald-600 text-white rounded-full px-3.5 py-1.5 text-xs font-semibold hover:bg-emerald-700"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" /> Approve & create user
                    </button>
                    <input
                      value={reasonById[r.id] || ""}
                      onChange={(e) => setReasonById((prev) => ({ ...prev, [r.id]: e.target.value }))}
                      placeholder="Reject reason (optional)"
                      data-testid={`admin-access-reason-${r.id}`}
                      className="flex-1 min-w-[160px] bg-white border border-slate-200 rounded-full px-3 py-1.5 text-xs"
                    />
                    <button
                      type="button"
                      onClick={() => reject(r)}
                      data-testid={`admin-access-reject-${r.id}`}
                      className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-3.5 py-1.5 text-xs font-semibold hover:bg-slate-50"
                    >
                      <XCircle className="w-3.5 h-3.5" /> Reject
                    </button>
                  </>
                ) : (
                  <span className="text-xs text-slate-500">
                    {r.status === "approved" ? "User created" : r.rejection_reason || "Rejected"} · {fmt(r.decided_at)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link to="/" className="text-xs text-slate-500 hover:text-slate-900">← Back to site</Link>
        </div>
      </Section>

      {credentialPopup && (
        <div
          data-testid="admin-credential-popup"
          className="fixed inset-0 z-50 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
        >
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setCredentialPopup(null)} />
          <div className="relative bg-white rounded-2xl border border-slate-200 shadow-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-5 h-5" />
              <h3 className="font-display text-lg font-bold">Clinic user created</h3>
            </div>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              Share these credentials with the clinic securely. The temporary password is shown only once.
            </p>
            <div className="mt-4 space-y-3">
              <CredRow label="Login URL" value={`${window.location.origin}/login`} onCopy={copyToClipboard} />
              <CredRow label="Email" value={credentialPopup.email} onCopy={copyToClipboard} />
              <CredRow label="Temporary password" value={credentialPopup.temp_password} onCopy={copyToClipboard} mono />
            </div>
            <button
              type="button"
              onClick={() => setCredentialPopup(null)}
              data-testid="admin-credential-popup-close"
              className="mt-5 w-full inline-flex items-center justify-center gap-1.5 bg-slate-900 text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-slate-800"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function NotifierHealthPanel({ data }) {
  // Renders nothing in the "no data" state — avoids cluttering the dashboard
  // on a fresh deployment before the first visitor submission has fired.
  if (!data || (!data.total_sent && !data.total_failed)) return null;

  const KIND_LABEL = {
    demo_request: "Demo request",
    contact_message: "Contact form",
    clinic_access_request: "Clinic access",
    vendor_application: "Vendor application",
  };
  const successPct = data.success_rate;
  const isHealthy = successPct === null || successPct >= 95;
  const isWarn = successPct !== null && successPct >= 75 && successPct < 95;
  // Tailwind JIT requires literal class strings — avoid template-string dynamic classes.
  const PILL_STYLES = {
    healthy: "bg-emerald-50 text-emerald-700 border-emerald-200",
    warn: "bg-amber-50 text-amber-700 border-amber-200",
    fail: "bg-rose-50 text-rose-700 border-rose-200",
  };
  const RING_STYLES = {
    healthy: "border-emerald-200",
    warn: "border-amber-200",
    fail: "border-rose-200",
  };
  const toneKey = isHealthy ? "healthy" : isWarn ? "warn" : "fail";
  const ToneIcon = isHealthy ? CheckCircle2 : AlertTriangle;

  return (
    <div
      data-testid="admin-notifier-health-panel"
      className={`rounded-2xl border bg-white p-5 ${RING_STYLES[toneKey]}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400 inline-flex items-center gap-1.5">
            <Mail className="w-3.5 h-3.5" /> Notification delivery health
          </div>
          <h2 className="font-display text-xl font-bold text-slate-900 mt-1">
            Last 7 days
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Visitor confirmations + admin alerts dispatched via Resend.
          </p>
        </div>
        <div
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold tabular-nums border ${PILL_STYLES[toneKey]}`}
          data-testid="admin-notifier-health-rate"
        >
          <ToneIcon className="w-3 h-3" />
          {successPct !== null ? `${successPct}%` : "—"} delivered
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4">
        <div className="rounded-xl bg-slate-50 p-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
            Delivered
          </div>
          <div
            className="font-display text-2xl font-bold tabular-nums text-emerald-700 mt-0.5"
            data-testid="admin-notifier-health-sent"
          >
            {data.total_sent}
          </div>
        </div>
        <div className="rounded-xl bg-slate-50 p-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
            Failed
          </div>
          <div
            className={`font-display text-2xl font-bold tabular-nums mt-0.5 ${data.total_failed > 0 ? "text-rose-700" : "text-slate-400"}`}
            data-testid="admin-notifier-health-failed"
          >
            {data.total_failed}
          </div>
        </div>
      </div>

      <div className="space-y-1.5">
        {(data.per_kind || [])
          .sort((a, b) => {
            const aTot = a.admin_sent + a.admin_failed + a.submitter_sent + a.submitter_failed;
            const bTot = b.admin_sent + b.admin_failed + b.submitter_sent + b.submitter_failed;
            return bTot - aTot;
          })
          .map((k) => {
            const sent = k.admin_sent + k.submitter_sent;
            const failed = k.admin_failed + k.submitter_failed;
            const total = sent + failed;
            const pct = total ? Math.round((sent / total) * 100) : 0;
            return (
              <div
                key={k.kind}
                data-testid={`admin-notifier-health-row-${k.kind}`}
                className="flex items-center gap-3 text-xs"
              >
                <div className="w-32 font-semibold text-slate-700 truncate">
                  {KIND_LABEL[k.kind] || k.kind}
                </div>
                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${pct === 100 ? "bg-emerald-500" : pct >= 75 ? "bg-amber-500" : "bg-rose-500"}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="text-[10px] tabular-nums text-slate-500 w-24 text-right">
                  {sent}/{total} ({pct}%)
                </div>
              </div>
            );
          })}
      </div>

      {data.last_error && (
        <div
          data-testid="admin-notifier-health-last-error"
          className="mt-3 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2"
        >
          <span className="font-bold uppercase tracking-wider mr-1">Last error:</span>
          {data.last_error.length > 200
            ? `${data.last_error.slice(0, 200)}…`
            : data.last_error}
        </div>
      )}
    </div>
  );
}

function VendorCoverageWidget({ vendors, applications, onRecruit }) {
  // Bucket coverage gives admins a one-glance answer to "where do we need to
  // recruit next?" — same data the public /vendors marketing tiles use, with
  // a thin-coverage warning when a program has ≤2 vendors (rule of thumb:
  // marketing tiles read as credible at 3+ partners per program). When thin,
  // we surface a "Recruit candidates" CTA that opens a drawer of pipeline
  // applicants matching that bucket — closing signal → action in one tap.
  const BUCKETS = ["sleep", "metabolic", "hormone", "diagnostics", "wellness"];
  const STYLES = {
    sleep: "bg-indigo-50 text-indigo-700 border-indigo-200",
    metabolic: "bg-emerald-50 text-emerald-700 border-emerald-200",
    hormone: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
    diagnostics: "bg-sky-50 text-sky-700 border-sky-200",
    wellness: "bg-amber-50 text-amber-700 border-amber-200",
  };
  const active = (vendors || []).filter((v) => v.status === "active");
  const counts = {};
  let noBucket = 0;
  for (const v of active) {
    if (v.program_bucket) counts[v.program_bucket] = (counts[v.program_bucket] || 0) + 1;
    else noBucket += 1;
  }
  const thin = BUCKETS.filter((b) => (counts[b] || 0) <= 2);
  const totalActive = active.length;

  // Pipeline candidate count per bucket — only "recruitable" statuses count
  // (submitted + needs_clarification) so the CTA reflects deals you can act on.
  const recruitableByBucket = {};
  for (const a of applications || []) {
    if (a.status !== "submitted" && a.status !== "needs_clarification") continue;
    if (!a.program_bucket) continue;
    recruitableByBucket[a.program_bucket] = (recruitableByBucket[a.program_bucket] || 0) + 1;
  }

  return (
    <div
      data-testid="admin-vendor-coverage-widget"
      className="rounded-2xl border border-slate-200 bg-white p-5"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400 inline-flex items-center gap-1.5">
            <Boxes className="w-3.5 h-3.5" /> Vendor coverage
          </div>
          <h2 className="font-display text-xl font-bold text-slate-900 mt-1">
            Program-bucket distribution
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {totalActive} active vendor{totalActive === 1 ? "" : "s"} across {BUCKETS.length} programs.
            {thin.length > 0 && (
              <>
                {" "}
                <span className="text-rose-700 font-semibold inline-flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Coverage thin in {thin.join(", ")}.
                </span>
              </>
            )}
          </p>
        </div>
        <Link
          to="/admin/vendors-directory"
          data-testid="admin-vendor-coverage-open"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-900 bg-white border border-slate-200 rounded-full px-3.5 py-1.5 hover:bg-slate-50 shrink-0"
        >
          Open directory <ArrowUpRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {BUCKETS.map((b) => {
          const n = counts[b] || 0;
          const isThin = n <= 2;
          const candidates = recruitableByBucket[b] || 0;
          return (
            <div
              key={b}
              data-testid={`admin-vendor-coverage-${b}`}
              className={`relative rounded-xl border p-3 ${STYLES[b]} ${isThin ? "ring-2 ring-rose-300" : ""}`}
            >
              <Link
                to={`/admin/vendors-directory?bucket=${b}`}
                className="block hover:opacity-80 transition-opacity"
              >
                <div className="text-[11px] uppercase tracking-[0.16em] font-bold capitalize inline-flex items-center gap-1">
                  {b}
                  {isThin && <AlertTriangle className="w-3 h-3 text-rose-700" />}
                </div>
                <div className="font-display text-2xl font-bold tabular-nums mt-0.5">{n}</div>
                <div className="text-[10px] uppercase tracking-wider opacity-70 mt-0.5">
                  {n === 1 ? "partner" : "partners"}
                </div>
              </Link>
              {isThin && candidates > 0 && (
                <button
                  type="button"
                  onClick={() => onRecruit(b)}
                  data-testid={`admin-vendor-recruit-${b}`}
                  title={`${candidates} pipeline applicant${candidates === 1 ? "" : "s"} match this bucket. Open the shortlist.`}
                  className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-white/70 hover:bg-white border border-current rounded-full px-2 py-1 transition-colors"
                >
                  <UserPlus className="w-3 h-3" />
                  Recruit · {candidates}
                </button>
              )}
              {isThin && candidates === 0 && (
                <div
                  data-testid={`admin-vendor-recruit-empty-${b}`}
                  className="mt-2 text-[10px] italic opacity-60"
                >
                  No pipeline matches yet
                </div>
              )}
            </div>
          );
        })}
      </div>

      {noBucket > 0 && (
        <div
          data-testid="admin-vendor-coverage-nobucket"
          className="mt-3 inline-flex items-center gap-1.5 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-3 py-1.5"
        >
          <AlertTriangle className="w-3 h-3" />
          {noBucket} active vendor{noBucket === 1 ? "" : "s"} couldn't be bucketed and won't appear on /vendors. Open the directory to fix their category.
        </div>
      )}
    </div>
  );
}

function RecruitDrawer({ bucket, applications, onClose }) {
  // Pre-filtered shortlist of pipeline applications matching `bucket`. Ranked
  // by completion_pct DESC so the most submission-ready prospects sit on top —
  // these are the ones an admin can review + approve same-day without a
  // clarification round-trip.
  const candidates = (applications || [])
    .filter(
      (a) =>
        a.program_bucket === bucket &&
        (a.status === "submitted" || a.status === "needs_clarification"),
    )
    .sort((a, b) => (b.completion_pct || 0) - (a.completion_pct || 0));

  return (
    <div
      data-testid="admin-recruit-drawer"
      className="fixed inset-0 z-50 flex justify-end"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" />
      <aside
        className="relative w-full max-w-md h-full bg-white border-l border-slate-200 shadow-xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-400 inline-flex items-center gap-1.5">
              <UserPlus className="w-3 h-3" /> Recruit candidates
            </div>
            <h2 className="font-display text-lg font-bold text-slate-900 mt-0.5 capitalize">
              {bucket} · {candidates.length} pipeline applicant
              {candidates.length === 1 ? "" : "s"}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Ranked by submission completeness — most review-ready first.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-recruit-drawer-close"
            className="rounded-full p-1.5 hover:bg-slate-100 text-slate-500"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        {candidates.length === 0 ? (
          <div className="p-6 text-sm text-slate-500 text-center">
            No matching pipeline applicants. Try{" "}
            <Link
              to={`/admin/vendors-directory?bucket=${bucket}`}
              className="text-blue-700 hover:underline"
              onClick={onClose}
            >
              cold-outreach from the directory
            </Link>
            .
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {candidates.slice(0, 12).map((a) => {
              const co = a.company || {};
              const name = co.legal_name || "Untitled applicant";
              const loc = co.headquarters_location || "—";
              const completion = a.completion_pct || 0;
              return (
                <li
                  key={a.id}
                  data-testid={`admin-recruit-candidate-${a.id}`}
                  className="p-4 hover:bg-slate-50"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-display text-sm font-semibold text-slate-900 truncate">
                        {name}
                      </div>
                      <div className="mt-0.5 text-[11px] text-slate-500">
                        <span className="capitalize">{a.status.replace("_", " ")}</span> · {loc}
                      </div>
                      {a.ai_summary?.summary && (
                        <p className="mt-2 text-xs text-slate-600 line-clamp-2">
                          {a.ai_summary.summary}
                        </p>
                      )}
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${completion >= 80 ? "bg-emerald-500" : completion >= 50 ? "bg-amber-500" : "bg-rose-500"}`}
                            style={{ width: `${completion}%` }}
                          />
                        </div>
                        <span className="text-[10px] font-bold tabular-nums text-slate-600">
                          {completion}%
                        </span>
                      </div>
                    </div>
                    <Link
                      to={`/admin/vendor-pipeline?focus=${a.id}`}
                      data-testid={`admin-recruit-open-${a.id}`}
                      onClick={onClose}
                      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white bg-slate-900 hover:bg-slate-800 rounded-full px-2.5 py-1 shrink-0"
                    >
                      Review
                      <ExternalLink className="w-2.5 h-2.5" />
                    </Link>
                  </div>
                </li>
              );
            })}
            {candidates.length > 12 && (
              <li className="p-3 text-center">
                <Link
                  to={`/admin/vendor-pipeline?bucket=${bucket}`}
                  onClick={onClose}
                  className="text-xs font-semibold text-blue-700 hover:underline"
                >
                  View all {candidates.length} in pipeline →
                </Link>
              </li>
            )}
          </ul>
        )}
      </aside>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testId, tone }) {
  const toneCls =
    tone === "urgent"
      ? "bg-rose-50 border-rose-200"
      : "bg-white border-slate-200";
  const iconCls =
    tone === "urgent"
      ? "bg-rose-100 border-rose-200 text-rose-700"
      : "bg-slate-100 border-slate-200 text-slate-700";
  return (
    <div data-testid={testId} className={`rounded-2xl border p-4 flex items-center gap-3 ${toneCls}`}>
      <span className={`w-9 h-9 rounded-lg border flex items-center justify-center ${iconCls}`}>
        <Icon className="w-4 h-4" />
      </span>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400 truncate">{label}</div>
        <div className="font-display text-xl font-bold text-slate-900 tabular-nums">{value}</div>
      </div>
    </div>
  );
}

function PriorityBand({ tone, eyebrow, subtitle, children, testId }) {
  const palette = {
    urgent: { dot: "bg-rose-500", eyebrow: "text-rose-700", line: "border-rose-200" },
    active: { dot: "bg-blue-500", eyebrow: "text-blue-700", line: "border-blue-200" },
    reference: { dot: "bg-slate-400", eyebrow: "text-slate-500", line: "border-slate-200" },
  }[tone] || { dot: "bg-slate-400", eyebrow: "text-slate-500", line: "border-slate-200" };
  return (
    <div data-testid={testId} className="mt-8">
      <div className={`flex items-center gap-2 pb-2 border-b ${palette.line}`}>
        <span className={`w-2 h-2 rounded-full ${palette.dot}`} aria-hidden />
        <span className={`text-[11px] uppercase tracking-[0.22em] font-bold ${palette.eyebrow}`}>{eyebrow}</span>
        <span className="text-[11px] text-slate-400">·</span>
        <span className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500">{subtitle}</span>
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { label: "Pending", icon: Hourglass, cls: "bg-slate-100 text-slate-700 border-slate-200" },
    approved: { label: "Approved", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-100" },
    rejected: { label: "Rejected", icon: XCircle, cls: "bg-slate-100 text-slate-500 border-slate-200" },
  };
  const m = map[status] || map.pending;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.16em] font-bold rounded-full px-2 py-1 border ${m.cls}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

function CredRow({ label, value, onCopy, mono }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400">{label}</div>
      <div className="mt-1 flex items-center gap-2">
        <code className={`flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm break-all text-slate-800 ${mono ? "font-mono" : ""}`}>{value}</code>
        <button
          type="button"
          onClick={() => onCopy(value)}
          className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-full px-3 py-2 hover:bg-slate-50"
        >
          <Copy className="w-3.5 h-3.5" /> Copy
        </button>
      </div>
    </div>
  );
}


function EmailHealthPanel({ data, onDrain, onRefresh }) {
  const { status, queue } = data || {};
  const ok = !!status?.configured;
  const totalPending =
    (queue?.auth_outbox?.pending || 0) +
    (queue?.vendor_pipeline_outbox?.pending || 0) +
    (queue?.quick_actions?.pending || 0);
  const totalDelivered =
    (queue?.auth_outbox?.delivered || 0) +
    (queue?.vendor_pipeline_outbox?.delivered || 0) +
    (queue?.quick_actions?.delivered || 0);
  return (
    <div
      data-testid="admin-email-panel"
      className="rounded-2xl border border-slate-200 bg-white p-5"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400">
            Email delivery
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span
              data-testid="admin-email-status-pill"
              className={`inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.16em] font-bold rounded-full px-2 py-1 border ${
                ok
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-amber-50 text-amber-800 border-amber-200"
              }`}
            >
              {ok ? "Resend configured" : "Resend NOT configured"}
            </span>
            <span className="text-xs text-slate-500">
              from <span className="font-mono">{status?.from_email}</span>
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onRefresh}
            data-testid="admin-email-refresh-btn"
            className="text-xs text-slate-500 hover:text-slate-900 px-2 py-1"
          >
            Refresh
          </button>
          <button
            type="button"
            onClick={onDrain}
            data-testid="admin-email-drain-btn"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-900 bg-white border border-slate-200 rounded-full px-3.5 py-1.5 hover:bg-slate-50"
          >
            Drain queue now
          </button>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
        <QueueStat
          label="Magic-link emails"
          pending={queue?.auth_outbox?.pending || 0}
          delivered={queue?.auth_outbox?.delivered || 0}
        />
        <QueueStat
          label="Clarification emails"
          pending={queue?.vendor_pipeline_outbox?.pending || 0}
          delivered={queue?.vendor_pipeline_outbox?.delivered || 0}
        />
        <QueueStat
          label="One-click actions"
          pending={queue?.quick_actions?.pending || 0}
          delivered={queue?.quick_actions?.delivered || 0}
        />
      </div>
      {!ok ? (
        <p className="mt-3 text-[11px] leading-relaxed text-slate-500">
          Add <span className="font-mono">RESEND_API_KEY</span> to <span className="font-mono">/app/backend/.env</span> and verify the
          domain on resend.com — the drainer auto-ships everything queued ({totalPending} pending) on the next sweep.
        </p>
      ) : (
        <p className="mt-3 text-[11px] leading-relaxed text-slate-500">
          Background drainer runs every 60s. {totalDelivered} delivered all-time · {totalPending} pending.
        </p>
      )}
    </div>
  );
}

function QueueStat({ label, pending, delivered }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-3">
      <div className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-slate-400">{label}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="font-display text-xl font-bold text-slate-900 tabular-nums">{pending}</span>
        <span className="text-[11px] text-slate-500">pending</span>
      </div>
      <div className="text-[11px] text-emerald-700 font-semibold tabular-nums">
        {delivered} delivered
      </div>
    </div>
  );
}


/**
 * Compact health pill that summarises whether the shared clinic + vendor
 * portal logins are seeded in the current environment.
 */
function PortalLoginsStatusPill({ status }) {
  if (!status) {
    return (
      <span
        data-testid="admin-portal-logins-pill"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 bg-white border border-slate-200 rounded-full px-3 py-1.5"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-slate-300 animate-pulse" />
        Portal logins…
      </span>
    );
  }
  if (status._error) {
    return (
      <span
        data-testid="admin-portal-logins-pill"
        title="Could not reach the portal-logins status endpoint."
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-3 py-1.5"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
        Portal logins · error
      </span>
    );
  }
  const clinicOk = !!status.clinic?.present;
  const vendorOk =
    !!status.vendor?.present && !!status.vendor?.directory_record_linked;
  const present = (clinicOk ? 1 : 0) + (vendorOk ? 1 : 0);
  if (present === 2) {
    return (
      <span
        data-testid="admin-portal-logins-pill"
        title={`Clinic: ${status.clinic?.email}\nVendor: ${status.vendor?.email}\nVendor directory: ${status.vendor?.directory_record_name || "—"}`}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        Portal logins · live
      </span>
    );
  }
  return (
    <span
      data-testid="admin-portal-logins-pill"
      title={`Clinic ${clinicOk ? "OK" : "MISSING"} · Vendor ${vendorOk ? "OK" : "MISSING"}. Click "Seed portal logins" to provision.`}
      className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
      Portal logins · {present}/2
    </span>
  );
}


/**
 * Top-of-dashboard strip surfacing the health of every key integration
 * the platform depends on. Each pill renders state-appropriate colour
 * (green/amber/red) and a hover tooltip with detail. Refresh button
 * re-fires the underlying probes on demand.
 */
function SystemHealthStrip({ health, portalStatus, onRefresh }) {
  const items = [
    { id: "resend", label: "Email · Resend", data: health?.resend },
    { id: "twilio", label: "SMS · Twilio", data: health?.twilio },
    { id: "stripe", label: "Payments · Stripe", data: health?.stripe },
    { id: "emergent_llm", label: "AI · LLM key", data: health?.emergent_llm },
    { id: "portal_logins", label: "Portal logins", data: health?.portal_logins },
  ];

  const loading = !health;
  const hasError = health?._error;

  return (
    <div
      data-testid="admin-system-health-strip"
      className="mt-6 rounded-2xl border border-slate-200 bg-white p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-500">
            System health
          </span>
          {hasError && (
            <span className="text-[11px] text-red-600 font-medium">
              · could not load probes
            </span>
          )}
        </div>
        <button
          type="button"
          data-testid="admin-system-health-refresh-btn"
          onClick={onRefresh}
          className="text-[11px] font-semibold text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
        >
          <RefreshCcw className="w-3 h-3" /> Refresh
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {loading
          ? items.map((it) => (
              <SystemHealthPill
                key={it.id}
                testId={`system-health-${it.id}`}
                label={it.label}
                state="loading"
                detail=""
              />
            ))
          : items.map((it) => (
              <SystemHealthPill
                key={it.id}
                testId={`system-health-${it.id}`}
                label={it.label}
                state={it.data?.state || "missing"}
                detail={it.data?.detail || ""}
              />
            ))}
      </div>
    </div>
  );
}

function SystemHealthPill({ testId, label, state, detail }) {
  const tone =
    state === "ok"
      ? {
          dot: "bg-emerald-500",
          wrap: "text-emerald-700 bg-emerald-50 border-emerald-200",
        }
      : state === "partial"
        ? {
            dot: "bg-amber-500",
            wrap: "text-amber-700 bg-amber-50 border-amber-200",
          }
        : state === "missing"
          ? {
              dot: "bg-red-500",
              wrap: "text-red-700 bg-red-50 border-red-200",
            }
          : {
              dot: "bg-slate-300 animate-pulse",
              wrap: "text-slate-500 bg-white border-slate-200",
            };
  return (
    <span
      data-testid={testId}
      title={detail || label}
      className={`inline-flex items-center gap-1.5 text-xs font-semibold border rounded-full px-3 py-1.5 ${tone.wrap}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />
      {label}
    </span>
  );
}

